import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import EmailEditor, { EditorRef } from "react-email-editor";
import { toast } from "sonner";
import FloatingPanel, { EmailVariable, VARIABLE_OPTIONS } from "../crm/conversation/components/floating-panel";

type InsertPosition = "top" | "bottom";

export interface EmailEditorCoreRef {
  exportHtml: (callback: (data: { html: string; design: object }) => void) => void;
  loadDesign: (design: object) => void;
  insertVariable: (variableName: string) => void;
}

interface EmailEditorCoreProps {
  minHeight?: string;
  // Variables + their default ("Replace with") values, edited from the
  // Add Variables panel. Also fed to Unlayer as merge tags.
  variables?: EmailVariable[];
  onVariablesChange?: (vars: EmailVariable[]) => void;
  onReady?: () => void;
  // design = the Unlayer JSON, saved as the template's `content` so it
  // reopens as editable blocks.
  onChange?: (html: string, design?: object) => void;
  onTemplateGenerated?: (data: {
    html: string;
    subject?: string;
    header?: string;
    plainText?: string;
  }) => void;
}

export const EmailEditorCore = forwardRef<EmailEditorCoreRef, EmailEditorCoreProps>(
  ({ minHeight = "700px", variables, onVariablesChange, onReady, onChange, onTemplateGenerated }, ref) => {
    const emailEditorRef = useRef<EditorRef | null>(null);
    const [editorReady, setEditorReady] = useState(false);

    // Unlayer runs in a cross-origin iframe and has no "insert at cursor"
    // API, so variables reach the caret through its own merge tags: the
    // Merge Tags menu in the text toolbar and typing "{" both insert
    // right where the user is typing. Sample = the default value (the
    // editor's own preview shows it), else the tag itself.
    useEffect(() => {
      const editor = emailEditorRef.current?.editor;
      if (!editorReady || !editor) return;
      const names = [...VARIABLE_OPTIONS, ...(variables || []).map((v) => v.name).filter((n) => !VARIABLE_OPTIONS.includes(n))];
      const tags: Record<string, { name: string; value: string; sample: string }> = {};
      names.forEach((name) => {
        const label = name.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
        tags[name] = { name: label, value: `{{${name}}}`, sample: variables?.find((v) => v.name === name)?.defaultValue.trim() || `{{${name}}}` };
      });
      editor.setMergeTags(tags);
      editor.setMergeTagsConfig({ autocompleteTriggerChar: "{", sort: false });
    }, [editorReady, variables]);

    const createHtmlRow = (html: string) => ({
      cells: [1],
      columns: [{
        contents: [{
          type: "html",
          values: { html, containerPadding: "20px" }
        }],
        values: { backgroundColor: "", padding: "0px" }
      }],
      values: { padding: "0px" }
    });

    const insertHtmlBlock = (html: string) => {
      const editor = emailEditorRef.current?.editor;
      if (!editor) return;

      editor.exportHtml((data: any) => {
        const nextDesign = data?.design || {};
        nextDesign.body = nextDesign.body || {};
        nextDesign.body.rows = nextDesign.body.rows || [];
        nextDesign.body.rows.push(createHtmlRow(html));
        editor.loadDesign(nextDesign);
      });
    };

    // Used to append `<p>{{var}}</p>` as a new last block — never where the
    // user was typing. Copies it instead, to paste at the cursor.
    const insertVariable = (variableName: string) => {
      const trimmed = variableName.trim().replace(/[{}]/g, "");
      if (!trimmed) return;
      const tag = `{{${trimmed}}}`;
      navigator.clipboard
        ?.writeText(tag)
        .then(() => toast.success(`Copied ${tag}`, { description: "Click where you want it in your text and paste (⌘V / Ctrl+V)." }))
        .catch(() => toast.info(`Type ${tag} where you want it, or type { in a text block to pick it.`));
    };

    useImperativeHandle(ref, () => ({
      exportHtml: (callback) => emailEditorRef.current?.editor?.exportHtml(callback),
      loadDesign: (design) => emailEditorRef.current?.editor?.loadDesign(design as any),
      insertVariable,
    }));

    // Fixed editor options with proper theme type
    const editorOptions = {
      features: {
        stockImages: true,
        preview: true,
        undoRedo: true,
        textEditor: {
          spellChecker: true,
        },
      },
      appearance: {
        theme: "modern_light" as const, // Use 'as const' to fix type
      },
      displayMode: "email" as const,
      tools: {
        text: {
          enabled: true,
          properties: {
            fontFamily: {
              items: [
                { label: "Arial", value: "Arial, Helvetica, sans-serif" },
                { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
                { label: "Georgia", value: "Georgia, serif" },
                { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
                { label: "Times New Roman", value: "Times New Roman, serif" },
                { label: "Trebuchet MS", value: "Trebuchet MS, sans-serif" },
                { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
              ],
            },
          },
        },
      },
    };

    const handleInsertContent = (
      contentType: string,
      content: string | object,
      _position: InsertPosition = "bottom",
      metadata?: { subject?: string; header?: string; plainText?: string }
    ) => {
      const editor = emailEditorRef.current?.editor;
      if (!editor) return;

      if (typeof content === "object" && contentType === "template") {
        editor.loadDesign(content as any);
        return;
      }

      if (typeof content === "string" && content.trim()) {
        if (contentType === "variable") {
          insertVariable(content);
          return;
        }
        insertHtmlBlock(content);
        onTemplateGenerated?.({
          html: content,
          subject: metadata?.subject,
          header: metadata?.header,
          plainText: metadata?.plainText,
        });
      }
    };

    const handleEditorReady = () => {
      setEditorReady(true);
      onReady?.();

      const editor = emailEditorRef.current?.editor;
      if (editor && onChange) {
        editor.addEventListener("design:updated", () => {
          editor.exportHtml((data: any) => {
            onChange(data.html || "", data.design);
          });
        });
      }
    };

    return (
      <div className="relative w-full h-full" style={{ minHeight }}>
        <EmailEditor
          ref={emailEditorRef}
          minHeight={minHeight}
          options={editorOptions}
          onReady={handleEditorReady}
        />
        <FloatingPanel variables={variables} onVariablesChange={onVariablesChange} onInsertContent={handleInsertContent} />
      </div>
    );
  }
);

EmailEditorCore.displayName = "EmailEditorCore";
export default EmailEditorCore;